import axios, {
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { i18n } from '@/app/i18n'
import { API_BASE_URL } from '@/shared/api/base-url'
import { getAccessToken, refreshAccessToken } from '@/shared/api/auth-tokens'

type ApiRequestOptions = Omit<AxiosRequestConfig, 'baseURL' | 'data' | 'url'> & {
  body?: unknown
}

// Private flag used to guarantee at most one refresh-and-retry per request.
type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

// Public auth endpoints: never attach a token, and never try to refresh on their 401
// (a login 401 is bad credentials and must surface to the form).
const PUBLIC_AUTH_PATHS = ['/v1/auth/login', '/v1/auth/refresh']
const isPublicAuthPath = (url?: string) => Boolean(url && PUBLIC_AUTH_PATHS.some((path) => url.includes(path)))

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: 'application/json',
  },
})

apiClient.interceptors.request.use((config) => {
  config.headers.set('Accept-Language', i18n.global.locale.value)

  const token = getAccessToken()
  if (token && !isPublicAuthPath(config.url)) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }

  return config
})

// 401 -> single-flight refresh -> retry the original request once. The refresh itself is
// serialized in auth-tokens.ts, so parallel 401s share one refresh (auth.md §4.4).
apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error)

    const { config, response } = error
    if (!config || !response || response.status !== 401 || isPublicAuthPath(config.url)) {
      return Promise.reject(error)
    }

    const retryableConfig = config as RetryableConfig
    if (retryableConfig._retried) return Promise.reject(error)

    const newToken = await refreshAccessToken()
    if (!newToken) return Promise.reject(error)

    retryableConfig._retried = true
    return apiClient.request(retryableConfig)
  },
)

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(status: number, body: unknown) {
    super(`Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

const getResponseBody = <T>(response: AxiosResponse<T>) => {
  if (response.status === 204) return null as T
  return response.data
}

export const apiRequest = async <T>(path: string, options: ApiRequestOptions = {}): Promise<T> => {
  const { body, headers, ...requestConfig } = options

  try {
    // FormData (file uploads) must keep axios's own multipart Content-Type + boundary — only force
    // JSON for plain-object bodies.
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData

    const response = await apiClient.request<T>({
      ...requestConfig,
      url: path,
      data: body,
      headers: {
        ...headers,
        ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      },
    })

    return getResponseBody(response)
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new ApiError(error.response.status, error.response.data)
    }

    throw error
  }
}
